import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button, Divider } from '@material-ui/core';
import { useNavigate } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  root: {
    fontFamily: "'Inter', sans-serif",
    color: '#ffffff',
    padding: '26px 18px 0px',
    maxWidth: 980,
    margin: '0 auto',
    height: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    textAlign: 'left',
    [theme.breakpoints.down('sm')]: {
      padding: '10px 8px 0px',
    },
  },
  hero: {
    padding: '18px 18px 14px',
    borderRadius: 14,
    border: '1px solid rgba(77, 121, 255, 0.35)',
    background: 'radial-gradient(circle at 18% 18%, rgba(77,121,255,0.35), rgba(10,14,30,0.96))',
    boxShadow: '0 0 24px rgba(77,121,255,0.14)',
    marginBottom: 18,
    [theme.breakpoints.down('sm')]: {
      padding: '10px 10px 10px',
      marginBottom: 10,
    },
  },
  titleRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
  },
  title: {
    fontSize: '2.0rem',
    fontWeight: 900,
    letterSpacing: -0.4,
    lineHeight: 1.1,
    textShadow: '0 0 16px rgba(77, 121, 255, 0.55)',
  },
  subtitle: {
    marginTop: 8,
    color: '#cfd8ff',
    lineHeight: 1.6,
    fontSize: 13,
    maxWidth: 860,
    textAlign: 'left',
  },
  primaryButton: {
    backgroundColor: '#4d79ff',
    color: '#ffffff',
    textTransform: 'none',
    fontWeight: 800,
    borderRadius: 10,
    padding: '8px 12px',
    '&:hover': { backgroundColor: '#3e61cc' },
    [theme.breakpoints.down('sm')]: {
      fontSize: 11,
      padding: '6px 10px',
    },
  },
  section: {
    borderRadius: 14,
    border: '1px solid rgba(77, 121, 255, 0.22)',
    background: 'rgba(10, 14, 30, 0.82)',
    overflow: 'hidden',
    padding: 16,
    marginBottom: 14,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 900,
    letterSpacing: 0.35,
    textTransform: 'uppercase',
    color: '#e5e7ff',
    marginBottom: 10,
    textAlign: 'left',
  },
  body: {
    fontSize: 13,
    color: '#e8f5ff',
    lineHeight: 1.65,
    whiteSpace: 'pre-wrap',
    textAlign: 'left',
  },
  tableWrap: {
    overflowX: 'auto',
    marginTop: 12,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13,
    color: '#e8f5ff',
  },
  th: {
    textAlign: 'left',
    padding: '10px 10px',
    borderBottom: '1px solid rgba(255,255,255,0.12)',
    color: '#ffffff',
    fontWeight: 800,
    whiteSpace: 'nowrap',
  },
  td: {
    verticalAlign: 'top',
    padding: '10px 10px',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    lineHeight: 1.55,
  },
  pill: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 999,
    border: '1px solid rgba(77, 121, 255, 0.35)',
    background: 'rgba(77, 121, 255, 0.12)',
    color: '#dbe4ff',
    fontSize: 11,
    fontWeight: 800,
    marginRight: 6,
    marginBottom: 6,
  },
  callout: {
    marginTop: 10,
    padding: 12,
    borderRadius: 12,
    backgroundColor: 'rgba(5, 8, 20, 0.88)',
    border: '1px solid rgba(158, 247, 197, 0.28)',
    color: '#e8f5ff',
    fontSize: 13,
    lineHeight: 1.65,
    textAlign: 'left',
  },
  note: {
    marginTop: 10,
    padding: 12,
    borderRadius: 12,
    backgroundColor: 'rgba(5, 8, 20, 0.88)',
    border: '1px solid rgba(255, 180, 80, 0.35)',
    color: '#e8f5ff',
    fontSize: 13,
    lineHeight: 1.65,
    textAlign: 'left',
  },
  divider: {
    backgroundColor: 'rgba(255,255,255,0.10)',
    margin: '14px 0',
  },
}));

const columns = [
  {
    name: 'Name',
    description: "The token's display name, symbol, and a small colored dot indicating which chain it lives on (Ethereum, Arbitrum, Base, etc.). The star icon on the left is a watchlist toggle — click it to track the token across sessions. Clicking the token name opens the full detail page, where you can read the actual stories and theses the agent has generated.",
  },
  {
    name: 'Stories',
    description: "The number of AI-generated on-chain stories the agent created for this token within the currently selected Trend interval. If the Trend picker is set to \"24H,\" this column shows how many stories appeared in the last 24 hours. If it is set to \"1H,\" it shows only the last hour. This is the critical detail: the Stories count is not an all-time counter — it is a live, interval-aware signal that changes when you change the time lens.\n\nA blank cell means zero stories in this window. A green number means the agent detected at least one event worth writing about. Higher counts indicate more on-chain narrative activity, but more does not automatically mean better — a spike could reflect accumulation, whale movement, or a rug pull warning. The number tells you something is happening; the stories themselves tell you what.\n\nSort this column descending to surface the tokens with the most active narrative signal in whatever time window you have selected. This is the fastest way to find what the agent is paying attention to right now.",
  },
  {
    name: 'Theses',
    description: "The number of AI-generated investment theses for this token within the selected Trend interval. A thesis is a structured brief — LONG, SHORT, or AVOID — that the agent generates after it detects multiple converging stories pointing in the same direction for the same entity. If Stories are the field notes, Theses are the analyst's memo: fewer in number, higher in conviction, and explicitly directional.\n\nThe same interval-aware logic applies. A \"1\" in the Theses column under 24H Trend means the agent committed to a directional view on this token sometime in the last 24 hours, based on fresh on-chain evidence. Even a single thesis is significant — it means the agent saw enough converging signals to move from observation to judgment.\n\nClick the number to open the token's detail page and read the full thesis, including the conviction score, the entry signal, the invalidation condition, and the list of risk factors the agent considered.",
  },
  {
    name: 'Price',
    description: "Current USD price, sourced from E3D's multi-provider price engine. A green price indicates high-confidence data from a primary source. A yellow price indicates the price was obtained from a fallback source or has lower confidence — the token may be thinly traded, newly listed, or have limited price coverage. Yellow is not a warning about the token itself, just about the precision of this particular price point.",
  },
  {
    name: 'MCap',
    description: "Market capitalization in USD. When the number is green, it was calculated from verified circulating supply — the standard definition. When the number is yellow, E3D estimated MCap using total supply because verified circulating supply is not available for this token. Yellow MCap can overstate the real capitalization, especially for tokens with large locked, vesting, or burned portions of supply. Use yellow MCap figures as rough upper bounds, not precise valuations.",
  },
  {
    name: 'Total Supply',
    description: "The total supply of the token, formatted in compact notation (e.g. 1.2B, 450M). This is the broadest supply measure — it includes all minted tokens whether or not they are circulating, locked, or burned. Useful as a denominator when estimating fully diluted valuation or comparing supply structures across tokens.",
  },
  {
    name: 'Circ Supply',
    description: "Verified circulating supply when E3D can obtain it from a trusted source. Not all tokens have verified circulating supply data. When this cell is populated, the MCap column is calculated from it. When it is missing, MCap falls back to total supply (and shows yellow to indicate the estimate).",
  },
  {
    name: 'Price Trend',
    description: "A sparkline chart showing the token's price movement over the selected Trend interval. The sparkline covers the same time window as the Stories and Theses counts — if the Trend picker is set to 7D, the sparkline shows 7 days of price history. Green means the price ended the interval higher than it started. Red means it ended lower. The shape of the sparkline matters as much as the color: a green sparkline that spiked and collapsed is a different signal from one that climbed steadily.",
  },
  {
    name: 'Volume Trend',
    description: "A sparkline chart showing USD trading volume over the selected Trend interval. Same time-window logic as Price Trend. Volume sparklines help you distinguish between tokens that are moving on real participation and tokens that had a single thin-liquidity event. A rising volume sparkline behind a rising price sparkline is a healthier signal than price moving on flat or declining volume.",
  },
  {
    name: 'Fragility',
    description: "E3D's proprietary structural risk score, ranging from 0 (robust) to 100 (extremely fragile). Fragility measures how much you should trust the numbers you are seeing. It factors in price source quality (is the price coming from a deep DEX pool or a single low-liquidity pair?), supply anomalies (does circulating supply look unreliable?), and liquidity thinness (could a small trade move the price significantly?).\n\nThink of Fragility as the stress gauge on the dashboard. A score of 15 means the token's data infrastructure looks solid — the numbers probably mean what they say. A score of 75 means the foundation is shaky — the price could be unreliable, the MCap could be misleading, and any narrative signal from stories should be interpreted with extra skepticism.\n\nFragility does not measure whether a token is a good or bad investment. It measures whether you can trust the data well enough to make that judgment.",
  },
  {
    name: '% Change columns',
    description: "Configurable price-change columns showing percentage gain or loss over fixed intervals. The defaults are 12H and 24H, but you can toggle other intervals (1H, 6H, 7D, 30D) on or off from the \"% Columns\" control in the toolbar. A green up-arrow means the price rose in that interval. A red down-arrow means it fell. These columns are independent of the Trend picker — they always show the interval printed in their header, regardless of the selected Trend.",
  },
];

export default function PriceReferencePage() {
  const classes = useStyles();
  const navigate = useNavigate();

  return (
    <div className={classes.root}>
      <div className={classes.hero}>
        <div className={classes.titleRow}>
          <div>
            <div className={classes.title}>Price Page Reference</div>
            <div className={classes.subtitle}>
              A complete reference for every column, control, and setting on the E3D token screener — including the AI-powered Stories and Theses counts that are unique to E3D.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button className={classes.primaryButton} variant="contained" onClick={() => navigate('/price-guide')}>
              User Guide
            </Button>
            <Button className={classes.primaryButton} variant="contained" onClick={() => navigate('/price-use-cases')}>
              Use Cases
            </Button>
            <Button className={classes.primaryButton} variant="contained" onClick={() => navigate('/price')}>
              Back to Prices
            </Button>
          </div>
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>The dashboard at a glance</div>
        <div className={classes.body}>
          {'Every instrument in a cockpit has a reason. The /price page is the main dashboard of E3D — not just a table of tickers, but a live signal surface that layers price data, volume data, and AI-generated intelligence into a single view of the full 7,000+ token universe.\n\nThis reference covers every column, control, and behavior on the page. Use it when you see something and want to know exactly what it means, how it is calculated, and what you can do with it. If you want a guided walkthrough instead, start with the User Guide. If you want ready-made workflows, see Use Cases.'}
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>1) The column set</div>
        <div className={classes.body}>
          {'The table shows one row per token. Columns are divided into three layers: identity (what the token is), market data (what the price and volume are doing), and intelligence (what the AI agent has observed and concluded). The intelligence columns — Stories and Theses — are the ones you will not find on any other screener.\n\nEvery column is sortable. Clicking a column header sorts the entire universe server-side (not just the visible page), so you always see the true top and bottom of whatever you sort by.'}
        </div>
        <div className={classes.tableWrap}>
          <table className={classes.table}>
            <thead>
              <tr>
                <th className={classes.th} style={{ width: 150 }}>Column</th>
                <th className={classes.th}>What it means</th>
              </tr>
            </thead>
            <tbody>
              {columns.map((col) => (
                <tr key={col.name}>
                  <td className={classes.td} style={{ fontWeight: 800, color: '#ffffff', verticalAlign: 'top' }}>{col.name}</td>
                  <td className={classes.td}>{col.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className={classes.callout}>
          {'Hidden by default (re-enable via the Columns picker in the toolbar): Address (the token\'s contract address, useful for copy-pasting into wallets or explorers), Liquidity (total DEX liquidity in USD), Vol 24H (absolute 24-hour trading volume), FDV (fully diluted valuation), and Eff. Liq (effective liquidity — a tighter measure of real executable depth).'}
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>2) The Trend interval picker</div>
        <div className={classes.body}>
          {'The Trend picker is the single most important control on the page, and it is easy to overlook. It looks like a small button in the toolbar — "24H Trend" by default — but it controls three things simultaneously:\n\n1. The time window for the Price Trend and Volume Trend sparklines\n2. The time window for the Stories count — how many stories the agent created in the last N hours or days\n3. The time window for the Theses count — how many theses the agent created in the last N hours or days\n\nThis coupling is the key to understanding the screener. When you change the Trend picker from 24H to 1H, you are not just zooming in on sparklines. You are changing the Stories and Theses columns from "what happened today" to "what is happening right now." The entire intelligence layer of the table shifts underneath you.\n\nAvailable intervals: 1H, 6H, 12H, 24H (default), 7D, 30D.\n\nPractical consequence: sorting by Stories descending with 1H Trend selected shows you which tokens had on-chain narrative events in the last sixty minutes. That is a fundamentally different list from Stories sorted under 30D Trend, which shows accumulated narrative attention over the past month. The first is a radar sweep of live activity. The second is a heatmap of sustained attention. Both are useful — they answer different questions.\n\nThe server recalculates Story and Thesis counts using ClickHouse countIf() queries with a 10-minute refresh cache, so the numbers are both accurate and fast. You are not looking at stale data — a story that appeared 5 minutes ago will show up in the 1H count within the next refresh cycle.'}
        </div>
        <div className={classes.note}>
          {'Sorting by Stories with 1H Trend selected is like switching the radar from a city map to a live storm map. You are seeing where on-chain narrative energy is concentrated right now, not where it was yesterday.'}
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>3) Sorting and discovery</div>
        <div className={classes.body}>
          {'Click any column header to sort the table. Click the same header again to reverse direction. Sorting is server-side: the full 7,000+ token universe is re-ordered, not just the tokens currently visible on screen. This means the top result after sorting is genuinely the top result across the entire E3D coverage set.\n\nDefault sort: MCap descending (largest tokens first).\n\nThe most powerful sorts for discovery are the ones most screeners do not have:'}
        </div>
        <div style={{ marginTop: 10 }}>
          <span className={classes.pill}>Stories ↓</span>
          <span className={classes.pill}>Theses ↓</span>
          <span className={classes.pill}>Fragility ↑</span>
          <span className={classes.pill}>MCap ↑</span>
        </div>
        <div className={classes.body} style={{ marginTop: 10 }}>
          {'Stories descending — surfaces the tokens with the most narrative activity in the current Trend interval. This is the "what is the agent watching?" sort. Under 1H Trend, it shows live on-chain attention. Under 7D Trend, it shows sustained narrative focus.\n\nTheses descending — surfaces the tokens where the agent has formed structured conviction in the current interval. Since theses require multiple converging stories, any token appearing at the top of this sort has cleared a higher evidence bar than story count alone.\n\nFragility ascending — puts the most structurally fragile tokens at the top. Useful as a risk screen: scan this list to find tokens whose data you should not trust at face value, or combine it with high story counts to find narrative activity on shaky ground.\n\nMCap ascending — puts the smallest tokens first. Combine with Stories or Theses sorts (by first sorting one column, then visually scanning the other) to find small-cap tokens with active AI coverage. The agent covers the full universe without size bias, so micro-cap tokens with genuine on-chain activity show up here alongside the large caps.'}
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>4) Controls toolbar</div>
        <div className={classes.body}>
          {'The toolbar runs across the top of the table. Every control narrows or reshapes the view. Here is what each one does:\n\nSearch bar — full-text search across token name, ticker symbol, and contract address. Type a name to find a specific token, or paste a contract address to jump directly to it.\n\nWatchlist Only toggle — filters the table to show only tokens you have starred. When enabled, all other filters still apply on top. Use it to monitor a curated set without the noise of the full universe.\n\nAll Categories dropdown — filters by sector (DeFi, L1, L2, Meme, Stablecoin, Gaming, etc.). When you select a category, the table shows only tokens tagged in that sector. The story and thesis counts still reflect the selected Trend interval — you can see narrative activity within a single sector slice. Toggle between sectors to compare which one is generating the most agent attention.\n\nTrend picker — the interval selector described in Section 2. Changes the sparkline window and the Stories / Theses count window simultaneously.\n\n% Columns control — toggles which price-change interval columns are visible. Options: 1H, 6H, 12H, 24H, 7D, 30D. Enable the intervals you care about for your trading timeframe. These are independent of the Trend picker.\n\nColumns control — show or hide optional columns that are hidden by default: Address, Liquidity, Vol 24H, FDV, Eff. Liq. Turn these on when you need deeper market-structure data. Turn them off to reduce visual clutter when you are scanning for narratives.\n\nScreener — opens a panel with numeric range filters. Set minimum and/or maximum values for MCap, Price, Liquidity, Volume 24H, FDV, and Change%. This is the traditional screener within the screener — use it to carve out a universe (e.g. "MCap between $10M and $100M") and then sort by Stories or Theses within that universe.\n\nSectors — a tag-based sector filter. Similar to All Categories but allows multi-select. Use it to combine sector tags (e.g. "DeFi" + "L2") into a custom universe.\n\nX (Reset to defaults) — the emergency reset. One click clears all filters, search text, column visibility changes, sort order, and trend interval. The table returns to its default state: 24H Trend, MCap descending, all tokens visible, Stories and Theses columns showing. Use this when you have been exploring and want to start a fresh scan from a clean slate.'}
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionTitle}>5) Pagination and export</div>
        <div className={classes.body}>
          {'The table loads 300 tokens per page. When you scroll to the bottom, the next page loads automatically. Each page reflects the full sorted and filtered universe — the 301st token is genuinely the 301st token in whatever sort and filter combination you have active. The entire 7,000+ token set is accessible by scrolling. Most discovery work happens in the first one or two pages after a well-chosen sort.'}
        </div>
        <Divider className={classes.divider} />
        <div className={classes.body}>
          {'The CSV export icon (top right of the toolbar) exports the current table to a downloadable CSV file. The export respects your active filters, sort order, and column visibility — you get exactly what you see. Every visible column is included. Use this to pull data into a spreadsheet for further analysis, share a filtered token list with a colleague, or build a snapshot of narrative activity at a point in time.'}
        </div>
      </div>
    </div>
  );
}
